import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { API, toISODate, formatShortDate } from "@/lib/attendance";

export default function RecoveriesPanel({ token, readOnly }) {
  const [items, setItems] = useState([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = () => {
    axios.get(`${API}/admin/attendance/recoveries`, auth).then((r) => setItems(r.data))
      .catch(() => toast.error("Errore nel caricamento"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const patch = async (item, body) => {
    try {
      await axios.patch(`${API}/admin/attendance/${item.id}/recovery`, body, auth);
      toast.success(body.recovered ? "Segnata come recuperata" : "Riaperta");
      load();
    } catch (e) {
      if (!axios.isCancel(e)) toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const pending = items.filter((i) => !i.recovered).length;
  return (
    <div className="space-y-3" data-testid="recoveries-panel">
      <div className="text-sm text-slate-600">
        <strong data-testid="recoveries-pending-count">{pending}</strong> lezioni ancora da recuperare su {items.length} rimandate con recupero.
      </div>
      {!items.length && <div className="py-8 text-center text-sm text-slate-500">Nessuna lezione da recuperare.</div>}
      {items.map((it) => <RecoveryRow key={it.id} item={it} readOnly={readOnly} onPatch={(b) => patch(it, b)} />)}
    </div>
  );
}

function RecoveryRow({ item, readOnly, onPatch }) {
  const [date, setDate] = useState(item.recovery_date || toISODate(new Date()));
  return (
    <div data-testid={`recovery-row-${item.id}`}
      className={cn("bg-white rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3",
        item.recovered ? "border-green-200 opacity-75" : "border-amber-300")}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900">
          {item.group_nome} · {formatShortDate(item.date)} <span className="font-mono">{item.start_time}–{item.end_time}</span>
        </div>
        <div className="text-sm text-slate-600">Motivo: {item.postpone_reason} · segnata da <span className="capitalize">{item.teacher}</span></div>
        {item.recovered && (
          <div className="text-sm text-green-700 flex items-center gap-1 mt-0.5">
            <CheckCircle2 className="w-4 h-4" /> Recuperata il {formatShortDate(item.recovery_date)}
          </div>
        )}
      </div>
      {!readOnly && (item.recovered ? (
        <Button variant="outline" size="sm" onClick={() => onPatch({ recovered: false })} className="gap-1.5" data-testid={`recovery-reopen-${item.id}`}>
          <RotateCcw className="w-4 h-4" /> Riapri
        </Button>
      ) : (
        <div className="flex gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid={`recovery-date-${item.id}`} />
          <Button size="sm" onClick={() => onPatch({ recovered: true, recovery_date: date })} disabled={!date}
            className="bg-[#15803D] hover:bg-[#166534] h-10" data-testid={`recovery-mark-${item.id}`}>
            Recuperata
          </Button>
        </div>
      ))}
    </div>
  );
}
